<script setup lang="ts">
  import * as z from 'zod';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type {
    DateFieldType,
    DateInputProperties as DateInputPropsType,
  } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    DATE_FIELD_TYPE_OPTIONS,
    DATE_FIELD_TYPE_VALUES,
    DEFAULT_DATE_FIELD_TYPES,
    toggleDateFieldType,
  } from '../../../lib/date-field-types';
  import {
    Checkbox,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
  } from '@/shared/ui';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  type FormValues = {
    name: string;
    placeholder?: string;
    label?: string;
    fieldTypes: DateFieldType[];
    required?: boolean;
    labelPosition?: 'top' | 'left';
  };

  usePropertyForm<FormValues, DateInputPropsType>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
      placeholder: z.string().max(FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT).optional(),
      label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
      fieldTypes: z.array(z.enum(DATE_FIELD_TYPE_VALUES)).min(1, 'Selecione ao menos uma opção'),
      required: z.boolean().optional(),
      labelPosition: z.enum(['top', 'left']).optional(),
    }),
    extractInitialValues: (properties: DateInputPropsType | undefined): FormValues => ({
      name: properties?.name ?? '',
      placeholder: properties?.placeholder ?? '',
      label: properties?.label ?? '',
      fieldTypes: [...(properties?.fieldTypes ?? DEFAULT_DATE_FIELD_TYPES)],
      required: properties?.required ?? false,
      labelPosition: properties?.labelPosition ?? 'top',
    }),
    mapValuesToProperties: (values: FormValues, currentProperties: DateInputPropsType) => ({
      properties: {
        ...currentProperties,
        name: values.name || currentProperties.name,
        placeholder: values.placeholder || undefined,
        label: values.label || undefined,
        fieldTypes: [...values.fieldTypes],
        required: values.required ?? false,
        labelPosition: values.labelPosition,
      },
    }),
  });
</script>

<template>
  <div class="flex flex-col">
    <!-- ID do campo -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="nome_do_campo"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Tipo de campo -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ value, handleChange }"
        name="fieldTypes"
      >
        <FormItem class="flex flex-col gap-3">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Tipo de campo
          </FormLabel>
          <div class="flex flex-col gap-3">
            <label
              v-for="option in DATE_FIELD_TYPE_OPTIONS"
              :key="option.value"
              class="inline-flex items-center cursor-pointer gap-2"
              :data-testid="`date-field-type-${option.value}`"
            >
              <Checkbox
                :id="`date-field-type-${props.elementId ?? 'unknown'}-${option.value}`"
                :checked="(value ?? []).includes(option.value)"
                @toggle="handleChange(toggleDateFieldType(value ?? [], option.value))"
              />
              <span class="text-sm text-gray-11">{{ option.label }}</span>
            </label>
          </div>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Texto auxiliar -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="placeholder"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Rótulo + Posicionamento -->
    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione a posição" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Obrigatória -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
